import Util from "./Util";
import consola, {LogLevel} from "consola";
import BatchTrackingHelper from "./BatchTrackingHelper";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

type State = {
  hasBeenPaused: boolean;
  isFullscreen: boolean;
  currentTime: number;
  initialized: boolean;
  lastPosition: number;
}

class TrackingHelper {
  static state: State

  static init() {
    // - on initialise le state
    TrackingHelper.state = {
      hasBeenPaused: false,
      isFullscreen: false,
      currentTime: 0,
      initialized: false,
      lastPosition: 0,
    }

    this.initializeClickListeners()
    this.initializeDisplayListeners()
    this.initializeScrollListeners()

    consola.trace('[TrackingHelper] initialized')
  }

  static internalSearchResultClick = (element: HTMLElement) => {
    const keyword = element.dataset.hitIseKeyword ?? ''
    const clickRank = element.dataset.hitIseRank
    const page = element.dataset.hitIsePage

    const data = {
      event: 'ise_click',
      ise_keyword: keyword,
      ise_click_rank: parseInt(clickRank),
      ise_page: parseInt(page),
    }

    this.sendEvent(data)
  }

  static internalSearchResultDisplay = (keyword: string, page: number) => {
    const data = {
      event: 'ise_display',
      ise_keyword: keyword,
      ise_page: page,
    }

    this.sendEvent(data)
  }

  static handleHitClick = (e: Event) => {
    // e.preventDefault();
    const target = e.target as HTMLElement;
    const element = target.closest('[data-hit-click]') as HTMLElement;

    if (element !== null) {
      const isISE = element.dataset.hitClick === 'ise'
      if (isISE) {
        this.internalSearchResultClick(element as HTMLElement)
      } else {
        this.clickAction(element as HTMLElement)
      }

      // - on bloque le click si demandé
      if (Util.isDebugClick()) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
  }

  static clickAction = (element: HTMLElement) => {
    const type = element.dataset.hitType ?? 'navigation'
    const name = element.getAttribute('title') ?? element.dataset.hitName ?? 'unknown'
    const chapter1 = element.dataset.hitChapter1 ?? ''
    const chapter2 = element.dataset.hitChapter2 ?? ''
    const chapter3 = element.dataset.hitChapter3 ?? ''

    const data = {
      event: 'click',
      click_type: type,
      click: name,
      click_chapter1: chapter1,
      click_chapter2: chapter2,
      click_chapter3: chapter3,
    }

    // - on envoie l'event
    this.sendEvent(data)
  }

  static displayAction = (element: HTMLElement) => {
    const type = element.dataset.hitDisplayType ?? 'section_display'
    const name = element.dataset.hitDisplay ?? 'unknown'

    const data = {
      event: type,
      section_name: name,
    }

    consola.trace('[TrackingHelper] displayAction', data)

    // - on envoie l'event
    this.sendEvent(data)
  }

  private static initializeClickListeners() {
    document.addEventListener('click', this.handleHitClick)
  }

  private static initializeDisplayListeners() {
    const blocsToTrack = document.querySelectorAll('[data-hit-display]');

    // Observer pour chaque bloc
    const _this = this
    blocsToTrack.forEach((bloc) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            _this.displayAction(entry.target as HTMLElement);

            // Une fois que l'événement est envoyé, on arrête d'observer
            observer.disconnect();
          }
        });
      });

      observer.observe(bloc);
    });
  }

  private static initializeScrollListeners() {
    const scrollsToTracks = document.querySelectorAll('[data-hit-scroll]');
    let scrollsValues = [];

    // - on récupère toutes les valeurs de scrolls
    scrollsToTracks.forEach( (element: HTMLElement) => {
      const values = (element.dataset.hitScroll ?? '').split('|');
      // - pour toutes les valeurs si elles ne sont pas dans le scrollsToTracksObserver on les ajoute
      values.forEach(function (value) {
        if (!scrollsValues.includes(parseInt(value))) {
          scrollsValues.push(parseInt(value));
        }
      });
    });

    // - on trie les valeurs
    scrollsValues.sort((a, b) => {
      return a - b;
    });

    // - si le tableau n'est pas vide on ajoute l'observer
    if (scrollsValues.length > 0) {
      const scrollHits = scrollsValues.map(function() { return false; });
      const throttleDelay = 100; // Delay in milliseconds
      let lastExecution = Date.now() - throttleDelay;

      const checkScroll = () => {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrollPosition = window.scrollY || window.pageYOffset;
        const scrollPercent = (scrollPosition / scrollHeight) * 100;

        // - on parcourt toutes les valeurs de scroll, si on a pas encore envoyé l'event et que le scroll est supérieur ou égal à la valeur on envoie l'event
        for(let i = 0; i < scrollsValues.length; i++) {
          if(!scrollHits[i] && scrollPercent >= scrollsValues[i]) {
            scrollHits[i] = true;
            this.sendEvent({
              event: 'page_scroll',
              scroll_rate: scrollsValues[i],
            });

            // Batch SDK : envoyer read_article quand le scroll atteint 90%
            if (scrollsValues[i] >= 90) {
              BatchTrackingHelper.trackReadArticle();
            }
          }
        }
      };

      // - on ajoute l'event listener sur le scroll avec un throttle pour éviter de trop faire d'appels
      window.addEventListener('scroll', function() {
        if (Date.now() - lastExecution >= throttleDelay) {
          lastExecution = Date.now();
          checkScroll();
        }
      });

      consola.trace('[TrackingHelper] initializeScrollListeners', scrollsValues)
    }
  }

  private static sendEvent = (data: TrackingData) => {
    // - on supprime les valeurs nulls
    // Object.keys(data).forEach(key => data[key] == null && delete data[key])

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(data)

    consola.trace('[TrackingHelper] sendEvent', data)
  }

  static handlePlayerListener = (player, module) => {
    player = { ...player, container: module}
    player.on(dailymotion.events.VIDEO_PAUSE, () => {
      TrackingHelper.state.hasBeenPaused = true
      this.handlePlayerData(player, 'av_pause')
    })

    player.on(dailymotion.events.VIDEO_PLAY, () => {
      if (TrackingHelper.state.hasBeenPaused) {
        this.handlePlayerData(player, 'av_resume')
      }
    })

    player.on(dailymotion.events.VIDEO_END, () => {
      this.handlePlayerData(player, 'av_stop')

      // Batch SDK : finished_content
      this.sendBatchContentEvent(player, 'finished_content')
    })
    player.on(dailymotion.events.PLAYER_VOLUMECHANGE, () => this.handlePlayerData(player, 'av_volume'))
    player.on(dailymotion.events.VIDEO_SEEKSTART, async () => {
      const { videoTime } = await player.getState()
      TrackingHelper.state.currentTime = videoTime * 1000
      TrackingHelper.state.lastPosition = videoTime * 1000
      this.handlePlayerData(player, 'av_seek_start', { 'av_old_cursor_position': TrackingHelper.state.currentTime })
    })

    player.on(dailymotion.events.VIDEO_SEEKEND, async () => {
      const { videoTime } = await player.getState()

      if (TrackingHelper.state.currentTime > videoTime * 1000) {
        this.handlePlayerData(player, 'av_seek_backward', {
          'av_old_cursor_position': TrackingHelper.state.lastPosition,
          'av_new_cursor_position': videoTime * 1000,
        })
      } else {
        this.handlePlayerData(player, 'av_seek_forward', {
          'av_old_cursor_position': TrackingHelper.state.lastPosition,
          'av_new_cursor_position': videoTime * 1000,
        })
      }

      TrackingHelper.state.currentTime = videoTime * 1000
      TrackingHelper.state.lastPosition = videoTime * 1000
    })

    player.on(dailymotion.events.PLAYER_PRESENTATIONMODECHANGE, () => {
      if (TrackingHelper.state.isFullscreen) {
        this.handlePlayerData(player, 'av_fullscreen_off')
      } else {
        this.handlePlayerData(player, 'av_fullscreen_on')
      }
      TrackingHelper.state.isFullscreen = !TrackingHelper.state.isFullscreen
    })

    player.on(dailymotion.events.VIDEO_START, () => {
      this.handlePlayerData(player, 'av_start')

      // Batch SDK : started_content
      this.sendBatchContentEvent(player, 'started_content')
    })
    player.on(dailymotion.events.VIDEO_BUFFERING, () => this.handlePlayerData(player, 'av_buffer_start'))
    player.on(dailymotion.events.AD_CLICK, async () => {
      const { adPosition } = await player.getState()
      this.handlePlayerData(player, 'av_ad_click', { 'av_ad_type': adPosition })
    })
    player.on(dailymotion.events.PLAYER_ERROR, () => this.handlePlayerData(player, 'av_error'))
    player.on(dailymotion.events.VIDEO_QUALITYCHANGE, async () => {
      const { videoQuality } = await player.getState()
      this.handlePlayerData(player, 'av_quality', { 'av_quality_selected': videoQuality })
    })

    player.on(dailymotion.events.PLAYER_START, () => {
      this.handlePlayerData(player, 'av_play')
      TrackingHelper.state.initialized = true
    })
  }

  /**
   * Envoie un événement Batch pour un contenu vidéo (started_content / finished_content).
   */
  static sendBatchContentEvent = async (player, eventName: string) => {
    try {
      const state = await player.getState()
      const category = player.container?.dataset?.category ?? ''
      const program = player.container?.dataset?.program ?? ''

      const attributes: Record<string, string> = {
        title: state.videoTitle ?? '',
        author: program,
        url: window.location.href,
        image: '',
        category: category,
        type: category === 'live' ? 'live' : 'replay',
      }

      if (eventName === 'started_content') {
        BatchTrackingHelper.trackStartedContent(attributes)
      } else {
        BatchTrackingHelper.trackFinishedContent(attributes)
      }
    } catch (e) {
      consola.trace('[TrackingHelper] sendBatchContentEvent error', e)
    }
  }

  static handlePlayerData = async (player, eventType, additionalKeys?) => {
    if (!TrackingHelper.state.initialized && (
      eventType !== 'av_buffer_start' &&
      eventType !== 'av_start' &&
      eventType !== 'av_play' &&
      eventType !== 'av_quality'
    )) {
      return
    }

    const state = await player.getState()

    const date = new Date(state.videoCreatedTime * 1000)
    const year = date.getFullYear()
    const month = date.getMonth() + 1 > 9 ? date.getMonth() + 1 : `0${date.getMonth() + 1}`
    const day = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`
    const formattedDate = `${year}-${month}-${day}`

    const category = player.container.dataset.category ?? ''
    const program = player.container.dataset.program ?? ''

    const data = {
      'event': eventType,
      'av_content_id': state.videoId, // exemple
      'av_content': state.videoTitle, // titre de vidéo
      'av_content_type': category, // exemple, ou 'Documentaire', etc.
      'av_content_duration': state.videoDuration * 1000, // exemple (en millisecondes)
      'av_publication_date': formattedDate,
      'av_show': program, // si disponible (exemple : l'émission Déshabillons-les)
      'av_broadcasting_type': category === 'live' ? 'Direct' : 'Replay', // ou 'Direct', etc.
      'av_player': 'Dailymotion',
      ...additionalKeys
    }

    this.sendEvent(data)
  }
}

export default TrackingHelper
